import type {
  RailingStyle,
  ShapeSurface,
  SurfaceBorder,
  Shape,
  ShapeBadge,
  ShapeImage,
  ShapeStyle,
  ShapeThemes,
  ShapeText,
  ShapeUrlAction,
} from "../../adapters/types";
import {
  parseShapeDecor,
  serializeShapeDecor,
} from "../../core/decor";
import { generateId } from "../../core/generateId";
import { isRailingStyle } from "../../core/railing";
import { isSurfaceBorder, readSurface } from "../../core/surface";
import { parseOpenings } from "../../core/shapeOpenings";
import {
  extractRawId,
  lngLatToMercator,
  mercatorToLngLat,
  parseLayeredDocument,
  type DefaultShapeStyles,
  type JsonFormatConnector,
  type LoadedData,
  type ParseJsonFeatureOptions,
  type ParseJsonOptions,
  type RawConfigurations,
  type RawDocument,
} from "./shared";

/**
 * Format v2 (voir BESOINS.md §8.2) : `formatVersion: 2`. Seule différence avec v1 (`./v1.ts`) — la
 * façon dont l'image d'une forme est lue :
 * - `extra.icon` (v1) est renommé `extra.image`, qui devient le SEUL champ image d'une forme :
 *   `extra.imageSrc` (image "directe", v1) disparaît en tant que champ séparé — il n'existe plus de
 *   notion d'image "directe" distincte d'une icône portée par un champ séparé, tout est plaqué en
 *   mode "contain" (voir `Shape.image`). `extra.image.svg` reprend le rôle de l'ancien
 *   `extra.imageSvg`. Une image directe est distinguée par `extra.type: "image"` (même champ que
 *   `"rectangle"`/`"circle"`/`"line"`, voir `Shape.shapeKind`) — PAS par un sous-champ de `extra.image`
 *   (contrairement à l'ancien `extra.image.isDirect`, retiré de `ShapeImage`) : lire `extra.type`
 *   suffit à distinguer TOUS les types de forme d'un coup, image compris. Un document déjà
 *   sauvegardé avec l'ancien marqueur `extra.image.isDirect: true` reste lu correctement (voir
 *   `parseJsonFeatureV2` ci-dessous) — c'est une rétrocompatibilité de LECTURE uniquement,
 *   `shapeToRawFeatureV2` n'écrit plus jamais que `extra.type: "image"`. `extra.image.rotateWithMap`
 *   (voir `Shape.image.rotateWithMap`) n'a pas d'équivalent v1, sans surprise : v1 reste gelé.
 * - `entity.entity.image` n'est plus lu du tout : pas d'image de repli implicite dérivée d'`entity`
 *   (contrairement à v1, toujours tentée — voir `v1.ts`'s `resolveV1Image`).
 *
 * Un seul champ image signifie aussi qu'il n'y a plus de priorité à arbitrer (contrairement à v1,
 * où `extra.imageSrc` explicite gagnait sur `extra.icon`, lui-même gagnant sur une image déduite
 * d'`entity`, voir `v1.ts`) : `extra.image` se mappe directement sur `Shape.image`.
 * - `properties.type` (v1, sibling de `properties.extra`) est déplacé sous `properties.extra.type`
 *   en v2 — même sémantique (`Shape.shapeKind`), juste regroupé avec le reste des attributs
 *   "additionnels" de la forme plutôt qu'au même niveau que `id`/`entity`.
 */

export interface RawFeaturePropertiesV2 {
  id?: number | string;
  extra?: {
    /** Voir `Shape.shapeKind` : `"rectangle"`/`"image"` distinguent respectivement un rectangle et
     * une forme-image d'un polygone libre — toute autre valeur (ou absence, hors cercle/ligne) est
     * traitée comme un polygone libre. `"image"` est aussi reconnu en LECTURE via l'ancien marqueur
     * `extra.image.isDirect` (rétrocompatibilité, voir le commentaire en tête de fichier et
     * `parseJsonFeatureV2`) — `shapeToRawFeatureV2` n'écrit lui plus jamais que `extra.type: "image"`.
     * Un cercle n'est PAS distingué via ce champ (contrairement à `"rectangle"`/`"image"`) : c'est
     * `geometry.type === "LineString"` qui en fait foi par défaut (voir `parseJsonFeatureV2`) —
     * `shapeToRawFeatureV2` écrit quand même `"circle"` ici au réexport, par cohérence avec les
     * autres valeurs, mais ce n'est jamais lu au chargement pour LUI. `"line"`, en revanche, DOIT
     * être explicite ici : une ligne (`shapeKind: "line"`) produit aussi `geometry.type:
     * "LineString"` (même sérialisation GeoJSON qu'un cercle), donc `extra.type: "line"` est le seul
     * signal qui la distingue d'un cercle — sans lui, un `LineString` reste interprété comme un
     * cercle (repli historique, conservé pour ne pas casser des données existantes qui n'ont jamais
     * porté ce marqueur). */
    type?: string;
    /** Objet 3D d'une feature `extra.type: "decor"` — voir `parseShapeDecor`. */
    decor?: unknown;
    lock?: boolean;
    /** Voir `Shape.walkable`. Absent : traité comme `false` (pas de notion de pathfinding en v1). */
    walkable?: boolean;
    /** Voir `Shape.badge`. */
    badge?: ShapeBadge;
    /** Voir `Shape.walled`. */
    walled?: boolean;
    /** Voir `Shape.openings` — relu sans confiance, voir `parseOpenings`. */
    openings?: unknown;
    /** Voir `Shape.pathPriority`. Absent : aucun effet (pas de notion de pathfinding en v1). */
    pathPriority?: number;
    height?: number;
    elevation?: number;
    /** Voir `Shape.depth`. */
    depth?: number;
    /** Voir `Shape.railing` ; `true` est la finition vitrée, la seule d'avant les autres. */
    railing?: boolean | RailingStyle;
    surface?: ShapeSurface;
    surfaceBorder?: SurfaceBorder;
    /** Voir `Shape.water`. */
    water?: boolean;
    hoverHeight?: number;
    hoverElevation?: number;
    volumeOpacity?: number;
    minZoomOffset?: number;
    /** Voir `Shape.ground`. */
    ground?: boolean | "water" | "waterOnly";
    style?: ShapeStyle;
    /** Voir `Shape.hoverStyle`. Absent : la forme retombe sur le survol historique fixe de
     * l'adapter (non configurable). */
    styleHover?: ShapeStyle;
    /** Voir `Shape.action`. */
    action?: string;
    /** Voir `Shape.useActionIcon`. */
    useActionIcon?: boolean;
    /** Voir `Shape.urlAction`. */
    urlAction?: ShapeUrlAction;
    /**
     * Identifiant de la carte ouverte au clic — porté par `Shape.targetDocumentId`, renommé ici
     * parce que `extra` est écrit et relu côté consommateur, où une carte est bien une carte (le
     * mot est en revanche déjà pris par le canvas MapLibre dans `@tokamap/core`, voir
     * `Shape.targetDocumentId`). `target` évite de lire la clé comme "la carte qui porte cette
     * forme".
     */
    targetMapId?: string;
    /** Voir `Shape.text`/`ShapeText`. */
    text?: ShapeText;
    /** Voir `Shape.hoverText`. */
    textHover?: ShapeText;
    /**
     * Champ image UNIQUE en v2 : fusionne `extra.icon` et `extra.imageSrc` (v1, voir `v1.ts`) —
     * toujours affiché en mode "contain" (voir `Shape.image`), jamais étiré. `svg` reprend le rôle
     * de l'ancien `extra.imageSvg`. `isDirect`, quand présent, est un marqueur LEGACY (n'existe plus
     * sur `ShapeImage` lui-même) : `parseJsonFeatureV2` le lit encore pour la rétrocompatibilité de
     * lecture d'anciens documents, mais le marqueur canonique est désormais `extra.type: "image"`
     * (voir le commentaire en tête de fichier) — `shapeToRawFeatureV2` n'écrit plus jamais
     * `isDirect`.
     */
    image?: ShapeImage & { isDirect?: boolean };
  };
  entity?: unknown;
  /**
   * Thème de la forme (voir `Shape.themeId`), sibling d'`entity` et non `extra.themeId` : c'est une
   * colonne de la feature côté API, pas un attribut libre que le consommateur invente. Un thème est
   * une ressource à part entière, son id ne vient donc que de là.
   *
   * Typé `unknown` pour la même raison qu'`entity` : le producteur du document choisit comment il
   * rend le lien (id nu, objet, référence), et `rawThemeId` ramène les formes attendues à l'id.
   */
  theme?: unknown;
}

export interface RawFeatureV2 {
  type: "Feature";
  geometry: {
    type: string;
    coordinates: [number, number][][] | [number, number];
    properties?: RawFeaturePropertiesV2;
  };
}

/**
 * Convertit un unique "feature" du format exemple.json v2 en `Shape` — voir `v1.ts`'s
 * `parseJsonFeature` pour la contrepartie v1 (v1 reste gelé tel quel, voir CLAUDE.md : il ne
 * reconnaît ni les cercles ni les lignes, contrairement à v2 ici).
 *
 * `null` si `feature.geometry.type` n'est ni "Polygon" ni "LineString", ou si l'anneau n'a pas le
 * nombre de points attendu pour ce type : au moins 3 pour "Polygon" (polygone/rectangle), au moins
 * 2 pour "LineString" marqué `extra.type: "line"` (ligne — voir `Shape.coordinates`, polyligne
 * OUVERTE), exactement 2 pour tout autre "LineString" (cercle — voir `Shape.shapeKind`/
 * `Shape.coordinates`, `[centre, point de bord]`). Un cercle ET une ligne produisent tous les deux
 * `geometry.type: "LineString"` — `extra.type: "line"` est le seul signal qui les distingue (voir
 * `RawFeaturePropertiesV2.extra.type`) ; sans lui, un `LineString` retombe sur `"circle"` (repli
 * historique).
 */
/**
 * Rétrocompatibilité avec l'ancien champ `extra.urlAction.openInNewTab` (avant l'ajout de
 * `openMode`/`confirmBeforeOpen` juste au-dessus) : une forme déjà sauvegardée avec ce champ
 * garderait sinon un `openMode` toujours `undefined` au rechargement (repli silencieux sur
 * `"new_tab"`, voir `Shape.urlAction`), ce qui inverserait sans prévenir un `openInNewTab: false`
 * explicitement choisi. `shapeToRawFeatureV2` ci-dessous n'écrit plus jamais ce champ pour une
 * nouvelle sauvegarde — cette conversion ne s'applique donc qu'aux documents écrits avant ce
 * changement.
 */
function resolveUrlAction(rawUrlAction: (ShapeUrlAction & { openInNewTab?: boolean }) | undefined): ShapeUrlAction | undefined {
  if (!rawUrlAction) return undefined;
  if (rawUrlAction.openMode !== undefined) return rawUrlAction;
  return { ...rawUrlAction, openMode: rawUrlAction.openInNewTab === false ? "same_tab" : "new_tab" };
}

export function parseJsonFeatureV2(
  feature: RawFeatureV2,
  options: ParseJsonFeatureOptions = {},
): Shape | null {
  const properties = feature.geometry.properties ?? {};
  const isPoint = feature.geometry?.type === "Point";
  const decor = isPoint && properties.extra?.type === "decor" ? parseShapeDecor(properties.extra.decor, mercatorToLngLat) : null;
  const isMarker = isPoint && !decor;
  const isLineString = feature.geometry?.type === "LineString";
  const isLine = isLineString && properties.extra?.type === "line";
  const isCircle = isLineString && !isLine;
  if (feature.geometry?.type !== "Polygon" && !isLineString && !isPoint) return null;
  if (isPoint && properties.extra?.type === "decor" && !decor) return null;
  const rings = isPoint ? markerRings(feature.geometry.coordinates) : (feature.geometry.coordinates as [number, number][][]);
  const ring = rings?.[0];
  if (!rings || !ring) return null;
  if (isCircle && ring.length !== 2) return null;
  if (isLine && ring.length < 2) return null;
  if (!isLineString && !isPoint && ring.length < 3) return null;

  const id = properties.id ?? generateId();
  // `src` vide : traité comme "pas d'image", même convention qu'en v1.
  const rawImage = properties.extra?.image;
  // Marqueur canonique `extra.type: "image"`, OU repli de lecture sur l'ancien marqueur
  // `extra.image.isDirect` (voir le commentaire en tête de fichier et celui de
  // `RawFeaturePropertiesV2.extra.image`) — un document déjà sauvegardé avant l'introduction de
  // `shapeKind: "image"` ne porte que ce second marqueur.
  const isImage = properties.extra?.type === "image" || !!rawImage?.isDirect;
  const rawDepth = properties.extra?.depth;
  const depth = !isLine && !isPoint && !isImage && isPositiveNumber(rawDepth) ? rawDepth : undefined;
  const holes = isLineString || isImage || isPoint ? [] : rings.slice(1).filter((hole) => hole.length >= 3);

  return {
    id: String(id),
    type: "polygon",
    shapeKind: decor
      ? "decor"
      : isMarker
      ? "marker"
      : isCircle
        ? "circle"
        : isLine
          ? "line"
          : isImage
            ? "image"
            : properties.extra?.type === "rectangle"
              ? "rectangle"
              : "polygon",
    coordinates: ring.map(mercatorToLngLat),
    ...(holes.length ? { holes: holes.map((hole) => hole.map(mercatorToLngLat)) } : {}),
    rotation: 0,
    style: properties.extra?.style,
    hoverStyle: properties.extra?.styleHover,
    themeId: rawThemeId(properties.theme),
    action: properties.extra?.action,
    useActionIcon: properties.extra?.useActionIcon,
    urlAction: resolveUrlAction(properties.extra?.urlAction as (ShapeUrlAction & { openInNewTab?: boolean }) | undefined),
    targetDocumentId: properties.extra?.targetMapId,
    text: properties.extra?.text,
    hoverText: properties.extra?.textHover,
    locked: properties.extra?.lock,
    walkable: properties.extra?.walkable,
    ...(properties.extra?.badge ? { badge: properties.extra.badge } : {}),
    ...(properties.extra?.walled !== undefined ? { walled: properties.extra.walled } : {}),
    ...(decor ? { decor } : {}),
    ...(isLineString || isImage || isPoint ? {} : optionalOpenings(parseOpenings(properties.extra?.openings))),
    pathPriority: properties.extra?.pathPriority,
    height: properties.extra?.height,
    elevation: properties.extra?.elevation,
    ...(depth ? { depth } : {}),
    ...optionalRailing(properties.extra?.railing, isImage || isPoint),
    ...optionalSurface(properties.extra?.surface, isLine || isImage || isPoint),
    ...optionalSurfaceBorder(properties.extra?.surfaceBorder, isLine || isImage || isPoint),
    ...(depth && properties.extra?.water === true ? { water: true } : {}),
    hoverHeight: properties.extra?.hoverHeight,
    hoverElevation: properties.extra?.hoverElevation,
    volumeOpacity: properties.extra?.volumeOpacity,
    minZoomOffset: properties.extra?.minZoomOffset,
    ...(properties.extra?.ground ? { ground: properties.extra.ground === "water" || properties.extra.ground === "waterOnly" ? properties.extra.ground : true } : {}),
    image: rawImage?.src || rawImage?.source
      ? {
          src: rawImage.src ?? "",
          ...(rawImage.source ? { source: rawImage.source } : {}),
          width: rawImage.width,
          height: rawImage.height,
          scale: rawImage.scale,
          radius: rawImage.radius,
          opacity: rawImage.opacity,
          rotation: rawImage.rotation,
          horizontalAlign: rawImage.horizontalAlign,
          verticalAlign: rawImage.verticalAlign,
          rotateWithMap: rawImage.rotateWithMap,
          minScreenPx: rawImage.minScreenPx,
          svg: rawImage.svg,
        }
      : undefined,
    layer: options.layer,
    order: options.order,
    entity: properties.entity,
  };
}

/**
 * Sens inverse de `parseJsonFeatureV2` — voir `v1.ts`'s `shapeToRawFeature` pour la
 * contrepartie v1 (qui n'a pas d'équivalent : v1 reste gelé, ni cercle ni ligne n'y sont jamais
 * réécrits). `shapeKind: "image"` produit `extra.type: "image"` (voir le commentaire en tête de
 * fichier) — jamais plus `extra.image.isDirect`, même pour une forme relue depuis un ancien document
 * qui portait encore ce marqueur : réenregistrer une telle forme migre silencieusement vers le
 * nouveau marqueur. `shapeKind: "circle"` produit `geometry.type: "LineString"` ET `extra.type:
 * "circle"`, ce dernier uniquement par cohérence avec `"rectangle"`/`"image"` — jamais lu au
 * rechargement (voir `RawFeaturePropertiesV2.extra.type`). `shapeKind: "line"` produit aussi
 * `geometry.type: "LineString"`, mais SON `extra.type: "line"` EST lu par `parseJsonFeatureV2` :
 * c'est le seul moyen de la distinguer d'un cercle, qui a la même représentation GeoJSON.
 */
/**
 * Ramène `properties.theme` à l'id du thème. Tolère l'id nu et l'objet qui le porte, parce que
 * `theme` est un LIEN : selon le producteur du document, un lien se rend aussi bien par son id que
 * par la ressource elle-même — même incertitude qu'`entity`, d'où son type `unknown`. Toute autre
 * valeur est traitée comme "pas de thème", jamais devinée : une forme retombe alors sur son propre
 * style, ce que `Shape.themeId` prévoit déjà pour un thème inconnu.
 */
function rawThemeId(theme: unknown): string | undefined {
  const id = theme !== null && typeof theme === "object" ? (theme as { id?: unknown }).id : theme;

  if (typeof id === "number") return String(id);

  return typeof id === "string" && id !== "" ? id : undefined;
}

export function shapeToRawFeatureV2(shape: Shape): RawFeatureV2 {
  const image = shape.image;
  const isPoint = shape.shapeKind === "marker" || shape.shapeKind === "decor";
  return {
    type: "Feature",
    geometry: {
      type: isPoint ? "Point" : shape.shapeKind === "circle" || shape.shapeKind === "line" ? "LineString" : "Polygon",
      // L'API refuse un Point en `[x, y]` : elle n'accepte que l'anneau d'un seul sommet `[[[x, y]]]`.
      coordinates: isPoint
        ? [[lngLatToMercator(shape.coordinates[0]!)]]
        : [shape.coordinates, ...(shape.holes ?? [])].map((ring) => ring.map(lngLatToMercator)),
      properties: {
        id: extractRawId(shape),
        extra: {
          ...(shape.shapeKind === "rectangle" ? { type: "rectangle" } : {}),
          ...(shape.shapeKind === "circle" ? { type: "circle" } : {}),
          ...(shape.shapeKind === "line" ? { type: "line" } : {}),
          ...(shape.shapeKind === "image" ? { type: "image" } : {}),
          ...(shape.shapeKind === "marker" ? { type: "marker" } : {}),
          ...(shape.shapeKind === "decor" && shape.decor ? { type: "decor", decor: serializeShapeDecor(shape.decor, lngLatToMercator) } : {}),
          ...(shape.locked !== undefined ? { lock: shape.locked } : {}),
          ...(shape.walkable !== undefined ? { walkable: shape.walkable } : {}),
          ...(shape.badge ? { badge: shape.badge } : {}),
          ...(shape.walled !== undefined ? { walled: shape.walled } : {}),
          ...(shape.openings?.length ? { openings: shape.openings } : {}),
          ...(shape.pathPriority !== undefined ? { pathPriority: shape.pathPriority } : {}),
          ...(shape.height !== undefined ? { height: shape.height } : {}),
          ...(shape.elevation !== undefined ? { elevation: shape.elevation } : {}),
          ...(shape.depth ? { depth: shape.depth } : {}),
          ...(shape.railing ? { railing: shape.railing === "glass" ? true : shape.railing } : {}),
          ...(shape.surface ? { surface: shape.surface } : {}),
          ...(shape.surfaceBorder ? { surfaceBorder: shape.surfaceBorder } : {}),
          ...(shape.depth && shape.water ? { water: true } : {}),
          ...(shape.hoverHeight !== undefined ? { hoverHeight: shape.hoverHeight } : {}),
          ...(shape.hoverElevation !== undefined ? { hoverElevation: shape.hoverElevation } : {}),
          ...(shape.volumeOpacity !== undefined ? { volumeOpacity: shape.volumeOpacity } : {}),
          ...(shape.minZoomOffset !== undefined ? { minZoomOffset: shape.minZoomOffset } : {}),
          ...(shape.ground ? { ground: shape.ground } : {}),
          ...(shape.style !== undefined ? { style: shape.style } : {}),
          ...(shape.hoverStyle !== undefined ? { styleHover: shape.hoverStyle } : {}),
          ...(shape.action !== undefined ? { action: shape.action } : {}),
          ...(shape.useActionIcon !== undefined ? { useActionIcon: shape.useActionIcon } : {}),
          ...(shape.urlAction !== undefined ? { urlAction: shape.urlAction } : {}),
          ...(shape.targetDocumentId !== undefined ? { targetMapId: shape.targetDocumentId } : {}),
          ...(shape.text !== undefined ? { text: shape.text } : {}),
          ...(shape.hoverText !== undefined ? { textHover: shape.hoverText } : {}),
          ...(image !== undefined
            ? {
                image: {
                  src: image.src,
                  ...(image.source ? { source: image.source } : {}),
                  width: image.width,
                  height: image.height,
                  scale: image.scale,
                  ...(image.radius ? { radius: image.radius } : {}),
                  opacity: image.opacity,
                  rotation: image.rotation,
                  horizontalAlign: image.horizontalAlign,
                  verticalAlign: image.verticalAlign,
                  minScreenPx: image.minScreenPx,
                  svg: image.svg,
                  ...(image.rotateWithMap ? { rotateWithMap: true } : {}),
                },
              }
            : {}),
        },
        entity: shape.entity,
        ...(shape.themeId !== undefined ? { theme: shape.themeId } : {}),
      },
    },
  };
}

/**
 * `configurations` v2 (voir BESOINS.md §8/§8.2) : `featureStyle` (v1, voir `v1.ts`) est renommé
 * `defaultShapeStyle` — même donnée (style par défaut des nouvelles formes dessinées), juste un nom
 * plus explicite (cohérent avec `TokaMapInstance.setDefaultShapeStyle`/`Shape.style`).
 * `migrateV1DocumentToV2` (voir `../migrations/v1ToV2.ts`) effectue ce renommage lors d'une
 * migration v1 -> v2. Pas de notion de style de survol par défaut ici (`Shape.hoverStyle` reste un
 * champ par-forme uniquement, voir `adapters/types.ts`).
 */
export interface RawConfigurationsV2 extends RawConfigurations {
  defaultShapeStyle?: ShapeStyle;
  /**
   * Table des thèmes référençables par `properties.theme` des formes (voir `ShapeTheme`). Lue
   * seulement : le producteur du document en est propriétaire (côté back-office les thèmes sont une
   * ressource à part, portée par le PROJET et non par la carte) — `serializeDocumentV2` ne l'écrit
   * jamais, elle traverse donc le document telle quelle sans être régénérée.
   */
  themes?: ShapeThemes;
}

/** Champs de `LoadedView` extraits de `configurations` en v2 — voir `DefaultShapeStyles`. */
function extractV2ViewFields(configurations: RawConfigurationsV2 | undefined): DefaultShapeStyles & {
  themes?: ShapeThemes;
} {
  return {
    defaultShapeStyle: configurations?.defaultShapeStyle,
    themes: configurations?.themes,
  };
}

function parseDocument(data: unknown, options: ParseJsonOptions): LoadedData {
  return parseLayeredDocument(
    data as RawDocument<RawFeatureV2, RawConfigurationsV2> | undefined,
    options,
    parseJsonFeatureV2,
    extractV2ViewFields,
  );
}

export const v2Connector: JsonFormatConnector = { version: 2, parseDocument };

/** `[x, y]` en GeoJSON standard, `[[[x, y]]]` tel que l'API enregistre et rend un point. */
function markerRings(coordinates: RawFeatureV2["geometry"]["coordinates"]): [number, number][][] | null {
  const point = Array.isArray(coordinates[0]) ? (coordinates as unknown[][][])[0]?.[0] : coordinates;
  if (!Array.isArray(point) || point.length !== 2) return null;
  const [x, y] = point;
  return typeof x === "number" && typeof y === "number" ? [[[x, y]]] : null;
}

function isPositiveNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function optionalRailing(value: unknown, flat: boolean): Pick<Shape, "railing"> {
  if (flat) return {};
  if (value === true) return { railing: "glass" };
  return isRailingStyle(value) ? { railing: value } : {};
}

function optionalSurface(value: unknown, unsupported: boolean): Pick<Shape, "surface"> {
  if (unsupported) return {};
  const surface = readSurface(value);
  return surface ? { surface } : {};
}

function optionalSurfaceBorder(value: unknown, unsupported: boolean): Pick<Shape, "surfaceBorder"> {
  return !unsupported && isSurfaceBorder(value) ? { surfaceBorder: value } : {};
}

function optionalOpenings(openings: Shape["openings"]): Pick<Shape, "openings"> {
  return openings ? { openings } : {};
}
